import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { KbdShortcuts } from '@/components/ui/KbdShortcuts'
import {
  ToolbarButton,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/components/ui/Toolbar'
import type { LucideIcon } from 'lucide-react'
import {
  BaselineIcon,
  BoldIcon,
  CaseSensitiveIcon,
  ChevronDownIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  ItalicIcon,
  ListChecksIcon,
  ListIcon,
  ListOrderedIcon,
  MinusIcon,
  RemoveFormattingIcon,
  StrikethroughIcon,
  TypeIcon,
} from 'lucide-react'

import { useEditorState } from '@tiptap/react'

import { CELL_HIGHLIGHT_COLORS, CELL_TEXT_COLORS } from '~shared/consts'

import { CellToolbarTooltip } from '@/features/rundown/cell/toolbar/CellToolbar.tooltip'

import type { RegisterableHotkey } from '@tanstack/react-hotkeys'
import type { Editor } from '@tiptap/core'

type StyleBlock = {
  id: string
  label: string
  icon: LucideIcon
  isActive: (editor: Editor) => boolean
  command: (editor: Editor) => void
  shortcut: RegisterableHotkey[]
}

export const STYLE_BLOCKS = [
  {
    id: 'paragraph',
    label: 'Normal Text',
    icon: TypeIcon,
    isActive: (editor) =>
      !editor.isActive('heading') &&
      !editor.isActive('bulletList') &&
      !editor.isActive('orderedList') &&
      !editor.isActive('taskList'),
    command: (editor) => editor.chain().focus().setParagraph().run(),
    shortcut: ['Mod+Alt+0'],
  },
  {
    id: 'h1',
    label: 'Heading 1',
    icon: Heading1Icon,
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
    shortcut: ['Mod+Alt+1'],
  },
  {
    id: 'h2',
    label: 'Heading 2',
    icon: Heading2Icon,
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
    shortcut: ['Mod+Alt+2'],
  },
  {
    id: 'h3',
    label: 'Heading 3',
    icon: Heading3Icon,
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
    shortcut: ['Mod+Alt+3'],
  },
  {
    id: 'bulletList',
    label: 'Bullet List',
    icon: ListIcon,
    isActive: (editor) => editor.isActive('bulletList'),
    command: (editor) => editor.chain().focus().toggleBulletList().run(),
    shortcut: ['Mod+Shift+8'],
  },
  {
    id: 'orderedList',
    label: 'Number List',
    icon: ListOrderedIcon,
    isActive: (editor) => editor.isActive('orderedList'),
    command: (editor) => editor.chain().focus().toggleOrderedList().run(),
    shortcut: ['Mod+Shift+7'],
  },
  {
    id: 'taskList',
    label: 'Task List',
    icon: ListChecksIcon,
    isActive: (editor) => editor.isActive('taskList'),
    command: (editor) => editor.chain().focus().toggleTaskList?.().run(),
    shortcut: ['Mod+Shift+9'],
  },
] as const satisfies StyleBlock[]

export function CellToolbarEditorItems({ editor }: { editor: Editor }) {
  const { activeItem, hasActiveBlock } = useEditorState({
    editor: editor!,
    selector: (ctx) => {
      const activeEditor = ctx.editor

      const activeMatch = STYLE_BLOCKS.find((item) =>
        item.isActive(activeEditor),
      )

      const isModifiedStyle =
        activeEditor.isActive('heading') ||
        activeEditor.isActive('bulletList') ||
        activeEditor.isActive('orderedList') ||
        activeEditor.isActive('taskList')

      return {
        hasActiveBlock: isModifiedStyle,
        activeItem: activeMatch || STYLE_BLOCKS[0], // Fallback to paragraph
      }
    },
  })

  const { isBold, isItalic, isStrikethrough } = useEditorState({
    editor: editor!,
    selector: (ctx) => ({
      isBold: ctx.editor.isActive('bold'),
      isItalic: ctx.editor.isActive('italic'),
      isStrikethrough: ctx.editor.isActive('strike'),
    }),
  })

  const { activeTextColor, activeHighlightColor } = useEditorState({
    editor: editor!,
    selector: (ctx) => {
      const activeColor =
        ctx.editor.getAttributes('textStyle').color || '#ffffff'
      const activeHigh =
        ctx.editor.getAttributes('highlight').color || 'transparent'

      return {
        activeTextColor: activeColor,
        activeHighlightColor: activeHigh,
      }
    },
  })

  const BlockTriggerIcon = activeItem.icon

  return (
    <>
      <ToolbarGroup>
        {/* BLOCK STYLE */}
        <DropdownMenu>
          <CellToolbarTooltip label={`Text style: ${activeItem.label}`}>
            <ToolbarButton
              size="default"
              aria-label="Text style" // a11y: stable name
              aria-description={`Current style: ${activeItem.label}`} // a11y: contextual value
              data-active={hasActiveBlock}
              render={<DropdownMenuTrigger />}
            >
              <BlockTriggerIcon data-icon="inline-start" />
              <ChevronDownIcon
                data-icon="inline-end"
                className="text-muted-foreground"
              />
            </ToolbarButton>
          </CellToolbarTooltip>

          <DropdownMenuContent className="min-w-60">
            <DropdownMenuRadioGroup
              value={activeItem.id}
              onValueChange={(value) => {
                const selected = STYLE_BLOCKS.find((i) => i.id === value)
                if (!selected) return
                selected.command(editor) // Execute Tiptap command
              }}
            >
              {STYLE_BLOCKS.map((item) => {
                const ItemIcon = item.icon
                return (
                  <DropdownMenuRadioItem
                    key={item.id}
                    value={item.id}
                  >
                    <ItemIcon className="opacity-40 group-data-[state=checked]:opacity-100" />
                    <span>{item.label}</span>

                    <KbdShortcuts
                      hotkey={item.shortcut}
                      className="ml-auto"
                    />
                  </DropdownMenuRadioItem>
                )
              })}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* COLORS */}
        <DropdownMenu>
          <CellToolbarTooltip label="Text colors">
            <ToolbarButton
              size="default"
              aria-label="Text colors"
              render={<DropdownMenuTrigger />}
            >
              <div
                className="w-5 h-5 -m-1 mr-0 rounded-sm border border-black/15 flex items-center justify-center"
                style={{
                  backgroundColor:
                    activeHighlightColor !== 'transparent'
                      ? activeHighlightColor
                      : undefined,
                }}
              >
                <BaselineIcon
                  style={{
                    color: activeTextColor,
                  }}
                />
              </div>
              <ChevronDownIcon
                data-icon="inline-end"
                className="text-muted-foreground"
              />
            </ToolbarButton>
          </CellToolbarTooltip>

          <DropdownMenuContent className="min-w-52">
            <DropdownColorGrid
              id="text-color"
              label="Text color"
              colors={CELL_TEXT_COLORS}
              value={activeTextColor}
              onValueChange={(value) => {
                if (value === 'text-color-default') {
                  editor.chain().focus().unsetColor().run()
                } else {
                  const color = CELL_TEXT_COLORS.find((c) => c === value)
                  if (!color) return
                  // Execute Tiptap command
                  editor.chain().focus().setColor(color).run()
                }
              }}
            />

            <ToolbarSeparator
              orientation="horizontal"
              className="mt-2"
            />

            <DropdownColorGrid
              id="highlight-color"
              label="Highlight color"
              colors={CELL_HIGHLIGHT_COLORS}
              isHighlights={true}
              value={activeHighlightColor}
              onValueChange={(value) => {
                if (value === 'highlight-color-default') {
                  editor.chain().focus().unsetHighlight().run()
                } else {
                  const color = CELL_HIGHLIGHT_COLORS.find((c) => c === value)
                  if (!color) return
                  // Execute Tiptap command
                  editor.chain().focus().setHighlight({ color: color }).run()
                }
              }}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <CellToolbarTooltip
          label="Bold"
          shortcut="Mod+B"
        >
          <ToolbarButton
            aria-pressed={isBold}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon />
          </ToolbarButton>
        </CellToolbarTooltip>

        <CellToolbarTooltip
          label="Italic"
          shortcut="Mod+I"
        >
          <ToolbarButton
            aria-pressed={isItalic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon />
          </ToolbarButton>
        </CellToolbarTooltip>

        <CellToolbarTooltip
          label="Strikethrough"
          shortcut="Mod+Shift+S"
        >
          <ToolbarButton
            aria-pressed={isStrikethrough}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <StrikethroughIcon />
          </ToolbarButton>
        </CellToolbarTooltip>
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <CellToolbarTooltip
          label="Clear formatting"
          shortcut="Mod+\"
        >
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().unsetAllMarks().clearNodes().run()
            }
          >
            <RemoveFormattingIcon />
          </ToolbarButton>
        </CellToolbarTooltip>

        <CellToolbarTooltip
          label="Insert separator"
          shortcut={{
            key: '-',
            mod: true,
            shift: true,
          }}
        >
          <ToolbarButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            <MinusIcon />
          </ToolbarButton>
        </CellToolbarTooltip>

        <CellToolbarTooltip
          label="Toggle text case"
          shortcut="Mod+Shift+U"
        >
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCase().run()}
          >
            <CaseSensitiveIcon />
          </ToolbarButton>
        </CellToolbarTooltip>
      </ToolbarGroup>

      <ToolbarSeparator />
    </>
  )
}

function DropdownColorGrid({
  id,
  label,
  value,
  onValueChange,
  colors,
  isHighlights = false,
}: {
  id: string
  label: string
  colors: string[]
  value: string
  onValueChange: React.ComponentProps<
    typeof DropdownMenuRadioGroup
  >['onValueChange']
  isHighlights?: boolean
}) {
  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel
        id={id}
        className="tracking-wide uppercase select-none"
      >
        {label}
      </DropdownMenuLabel>

      <DropdownMenuRadioGroup
        value={value}
        onValueChange={onValueChange}
        aria-labelledby={id}
        className="grid grid-cols-5 gap-0.5 justify-items-center "
      >
        {/* Reset item */}
        <DropdownMenuRadioItem
          value={`${id}-default`}
          className="p-0 w-8 h-8 justify-center relative overflow-hidden group"
          aria-label={
            isHighlights ? 'Remove highlight' : 'Reset text to default color'
          }
        >
          <div className="absolute inset-0 p-1 flex items-center justify-center">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="w-full h-full text-red-500/80 stroke-3"
              aria-hidden="true"
            >
              <line
                x1="22"
                y1="2"
                x2="2"
                y2="22"
              />
            </svg>
          </div>
          {!isHighlights && (
            <span
              className="text-base font-bold text-neutral-400 mix-blend-screen z-10"
              aria-hidden="true"
            >
              A
            </span>
          )}
        </DropdownMenuRadioItem>

        {/* Colors item loop */}
        {colors.map((color) => (
          <DropdownMenuRadioItem
            key={`${id}-${color}`}
            value={color}
            className="p-0 w-8 h-8 justify-center aria-checked:bg-accent aria-checked:text-accent-foreground group relative"
            aria-label={
              isHighlights
                ? `Set highlight color to ${color}`
                : `Set text color to ${color}`
            }
            hideIndicator={true}
          >
            {isHighlights ? (
              <span
                className="w-5 h-5 rounded border border-white/10"
                style={{ backgroundColor: color }}
                title={color}
                aria-hidden="true"
              />
            ) : (
              <span
                className="text-base font-semibold"
                style={{ color: color }}
                title={color}
                aria-hidden="true"
              >
                A
              </span>
            )}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </DropdownMenuGroup>
  )
}
